/**
 * Global fetch interceptor: dispatches `api:unauthorized` on 401 responses
 * from API calls so AuthContext can refresh-or-logout.
 *
 * Why a monkey-patch: the codebase has hundreds of raw `fetch()` call sites
 * that don't go through apiClient/fetchWithActivity. Patching at the window
 * level catches every API call without per-site changes.
 */

const AUTH_ENDPOINTS = [
  '/api/auth/login',
  '/api/auth/refresh',
  '/api/auth/register',
  '/api/auth/logout',
  '/api/auth/2fa',
  '/api/auth/verify-2fa',
  '/api/auth/change-password',
];

function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINTS.some(ep => url.includes(ep));
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

let installed = false;

export function installFetchInterceptor(): void {
  if (installed) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await originalFetch(input, init);

    if (response.status === 401 && !isAuthEndpoint(urlOf(input))) {
      window.dispatchEvent(new CustomEvent('api:unauthorized', {
        detail: { url: urlOf(input), status: 401 },
      }));
    }

    return response;
  };
}
